/* L'unico posto da cui si parla col backend.
 *
 * Due regole che vale la pena non perdere di vista:
 *
 * 1. **Gli importi sono stringhe, sempre.** Il backend li accetta solo fra
 *    virgolette ("100.00") e li restituisce cosi', apposta: un numero JSON
 *    passa per il float e sbaglia i centesimi. In questo strato non esiste
 *    nessuna conversione a `number` su un importo, e non deve nascerne una.
 *
 * 2. **La sessione e' un cookie, non un token in memoria.** Non c'e' niente
 *    da mettere in un header: il cookie e' `httponly`, quindi il codice di
 *    questa pagina non puo' leggerlo nemmeno volendo. E' il motivo per cui
 *    lo stato "sono entrato" si chiede sempre a `/api/auth/me` e non si
 *    deduce da qualcosa salvato qui.
 */

import type {
  Attivita,
  Anzianita,
  BucoNumerazione,
  Cliente,
  DaPassare,
  Documento,
  FiltroRegistro,
  Imputazione,
  Incasso,
  Maturato,
  NomeModalita,
  Pratica,
  Rapporto,
  Registro,
  Scheda,
  SituazioneCliente,
  Tariffa,
  TestoFatturaSmart,
  VersamentoDaFatturare,
  FatturaDelVersamento,
  Gestione,
  PeriodoControllo,
  PrezzoConcordato,
  StatoAvvio,
  PersonaCosti,
  CostoRiga,
  AnteprimaTariffa,
  ModificaListino,
  PrestazioneSimile,
  CodiceDaRivedere,
  UsoAttivita,
} from "./dominio";

export type Ruolo = "AMMINISTRATORE" | "FATTURAZIONE" | "OPERATORE";

export interface Utente {
  identificativo: string;
  nome: string;
  email: string;
  ruolo: Ruolo;
  deve_cambiare_password: boolean;
  secondo_fattore_attivo: boolean;
  /** Obbligatoria e non ancora fatta: l'interfaccia manda alla pagina di
   * attivazione. Il blocco vero e' del server (403 su ogni rotta di lavoro). */
  deve_attivare_secondo_fattore: boolean;
  /** Dice com'e' il server, non la persona: il PDF dell'avviso e' acceso solo
   * dove c'e' Chromium (`PDF_ABILITATO`). Spento, il pulsante non compare. */
  pdf_abilitato: boolean;
}

/** Se mostrare il pulsante del PDF dell'avviso. **Solo un "si'" esplicito del
 * server lo accende**: un campo assente o una risposta di un server piu'
 * vecchio lasciano il pulsante spento, che e' il lato che non fa errori. */
export function pdfAvvisoAttivo(utente: Utente | null | undefined): boolean {
  return utente?.pdf_abilitato === true;
}

/** La risposta del login quando la password era giusta ma serve il codice a
 * tempo: niente sessione, solo un gettone di cinque minuti. */
export interface RichiestaSecondoFattore {
  richiede_secondo_fattore: true;
  token_accesso: string;
}

export interface AvvioSecondoFattore {
  chiave: string;
  uri: string;
  qr_svg_base64: string;
}

/** Un errore che arriva dal backend, col suo stato HTTP.
 *
 * Lo stato serve a chi chiama per distinguere i casi che non sono errori da
 * mostrare: un 401 su `/me` vuol dire "non sei entrato", non "e' andato
 * storto qualcosa". */
export class ErroreApi extends Error {
  constructor(
    readonly stato: number,
    messaggio: string,
    readonly campi: Record<string, string> = {},
  ) {
    super(messaggio);
    this.name = "ErroreApi";
  }
}

/** FastAPI mette in `detail` una stringa quando l'errore lo solleviamo noi,
 * e un elenco di oggetti quando a rifiutare e' la validazione di Pydantic.
 * Senza questa distinzione il secondo caso finirebbe a schermo come
 * "[object Object]", che e' il modo piu' rapido di rendere inutile un
 * messaggio d'errore. */
const NOMI_CAMPI: Record<string, string> = {
  denominazione: "Denominazione", codice: "Codice interno", sede: "Sede",
  indirizzo: "Indirizzo", codice_fiscale: "Codice fiscale", partita_iva: "Partita IVA",
  codice_destinatario: "Codice destinatario SDI", pec: "PEC",
  sostituto_imposta: "Sostituto d'imposta", territorialita: "Residenza ai fini IVA",
  cliente_id: "Cliente", pratica_id: "Pratica", pratiche_id: "Pratiche",
  giorno: "Giorno", minuti: "Durata", quantita: "Quantità",
  attivita_codice: "Prestazione", oggetto: "Su cosa", scopo: "Con quale risultato",
  data: "Data", data_incasso: "Data dell'incasso", importo: "Importo",
  descrizione: "Descrizione", prezzo: "Prezzo", oraria: "Tariffa oraria",
  dal: "Data iniziale", al: "Data finale", numero: "Numero", numero_nota: "Numero della nota",
  nome: "Nome", email: "Email", password: "Password", ruolo: "Ruolo",
  password_attuale: "Password attuale", nuova_password: "Nuova password",
};

function istruzione(v: { type?: string; ctx?: Record<string, unknown> }): string {
  const tipo = v.type ?? "";
  if (tipo === "missing") return "Compila questo campo.";
  if (tipo.startsWith("date")) return "Inserisci una data valida.";
  if (tipo.startsWith("int")) return "Inserisci un numero intero.";
  if (tipo.startsWith("bool")) return "Scegli Sì o No.";
  if (tipo === "string_too_short") return `Inserisci almeno ${v.ctx?.min_length ?? 1} caratteri.`;
  if (tipo === "string_too_long") return `Usa al massimo ${v.ctx?.max_length ?? "il limite di"} caratteri.`;
  if (tipo === "greater_than") return `Inserisci un valore maggiore di ${v.ctx?.gt}.`;
  if (tipo === "greater_than_equal") return `Inserisci un valore almeno pari a ${v.ctx?.ge}.`;
  if (tipo.startsWith("decimal") || tipo.startsWith("float")) return "Inserisci un importo valido.";
  return "Controlla il valore inserito e riprova.";
}

function erroreDa(corpo: unknown, stato: number): ErroreApi {
  // I dettagli di un guasto interno non aiutano a correggere un campo.
  if (stato >= 500) return new ErroreApi(stato, "Il programma non è riuscito a completare l'operazione. Riprova fra un momento.");
  if (typeof corpo === "object" && corpo !== null && "detail" in corpo) {
    const dettaglio = (corpo as { detail: unknown }).detail;
    if (typeof dettaglio === "string" && !["Not Found", "Unauthorized", "Forbidden", "Bad Request", "Too Many Requests"].includes(dettaglio)) {
      const campo = /^(oggetto|scopo):\s*(.+)$/s.exec(dettaglio);
      return new ErroreApi(stato, dettaglio, campo ? { [campo[1]!]: campo[2]! } : {});
    }
    if (Array.isArray(dettaglio)) {
      const campi: Record<string, string> = {};
      const righe: string[] = [];
      for (const voce of dettaglio) {
        if (typeof voce !== "object" || voce === null) continue;
        const v = voce as { loc?: unknown[]; type?: string; ctx?: Record<string, unknown> };
        const percorso = (Array.isArray(v.loc) ? v.loc : []).filter((p) => !["body", "query", "path"].includes(String(p)));
        const campo = percorso.filter((p) => typeof p === "string").at(-1);
        const indice = percorso.find((p) => typeof p === "number");
        const nome = NOMI_CAMPI[String(campo)] ?? "Un campo";
        const testo = istruzione(v);
        campi[percorso.join(".")] = testo;
        righe.push(`${nome}${typeof indice === "number" ? `, riga ${indice + 1}` : ""}: ${testo}`);
      }
      if (righe.length > 0) return new ErroreApi(stato, righe.join(" "), campi);
    }
  }
  const ripieghi: Record<number, string> = {
    400: "Controlla i dati inseriti e riprova.",
    401: "La sessione è scaduta. Accedi di nuovo per continuare.",
    403: "Il tuo ruolo non permette questa operazione. Chiedi all'amministratore dello studio.",
    404: "Questo elemento non è più disponibile. Torna all'elenco e aggiornalo.",
    409: "I dati sono cambiati. Aggiorna l'elenco prima di riprovare.",
    422: "Controlla i campi del modulo e riprova.",
    429: "Sono arrivate troppe richieste. Attendi un momento e riprova.",
  };
  return new ErroreApi(stato, ripieghi[stato] ?? "Non è stato possibile completare l'operazione. Riprova.");
}

/** L'evento che il client manda quando il server risponde 401 a una richiesta
 * che non e' di accesso: la sessione e' scaduta mentre la pagina era aperta
 * (dopo 60 minuti senza richieste, o 12 ore dall'ingresso). `ProvveditoreSessione`
 * lo ascolta e riporta alla pagina di accesso con l'avviso, invece di lasciare
 * una schermata che non funziona piu'. Come nel Portale. Le rotte `/api/auth/`
 * sono escluse: li' un 401 puo' voler dire "password sbagliata". */
export const SESSIONE_SCADUTA = "fatturazione:sessione-scaduta";

function segnalaSeScaduta(percorso: string, stato: number): void {
  // `window` non esiste nelle prove sotto Node: senza, il client non segnala.
  if (stato === 401 && !percorso.startsWith("/api/auth/") && typeof window !== "undefined") {
    window.dispatchEvent(new Event(SESSIONE_SCADUTA));
  }
}

async function richiesta<T>(
  metodo: string,
  percorso: string,
  corpo?: unknown,
): Promise<T> {
  let risposta: Response;
  try {
    risposta = await fetch(percorso, {
      method: metodo,
      headers: corpo === undefined ? {} : { "Content-Type": "application/json" },
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
      credentials: "same-origin",
    });
  } catch {
    // Rete caduta o server spento: non c'e' nessuno stato HTTP da leggere.
    // Lo 0 dice a chi chiama che il server non ha risposto affatto, che e'
    // diverso da un server che ha risposto male.
    throw new ErroreApi(0, "Il server non risponde. Controlla che sia acceso.");
  }

  if (risposta.status === 204) return undefined as T;

  const testo = await risposta.text();
  let letto: unknown = undefined;
  if (testo) {
    try {
      letto = JSON.parse(testo);
    } catch {
      letto = undefined;
    }
  }

  if (!risposta.ok) {
    segnalaSeScaduta(percorso, risposta.status);
    throw erroreDa(letto, risposta.status);
  }
  return letto as T;
}

/** Scarica un file che il server genera (un Excel): una richiesta con il cookie
 * di sessione, poi il file va su disco con il nome dato. **Non apre l'indirizzo
 * in una scheda**, cosi' un errore (sessione scaduta, ore di un altro) arriva
 * come messaggio qui invece che come testo grezzo in una pagina bianca. */
export async function scaricaFile(percorso: string, nomeFile: string): Promise<void> {
  let risposta: Response;
  try {
    risposta = await fetch(percorso, { credentials: "same-origin" });
  } catch {
    throw new ErroreApi(0, "Il server non risponde. Controlla che sia acceso.");
  }
  if (!risposta.ok) {
    segnalaSeScaduta(percorso, risposta.status);
    let letto: unknown = undefined;
    try {
      letto = await risposta.json();
    } catch {
      letto = undefined;
    }
    throw erroreDa(letto, risposta.status);
  }
  const blob = await risposta.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeFile;
  link.click();
  URL.revokeObjectURL(url);
}

export const api = {
  leggi: <T>(percorso: string) => richiesta<T>("GET", percorso),
  scrivi: <T>(percorso: string, corpo?: unknown) =>
    richiesta<T>("POST", percorso, corpo),
  sostituisci: <T>(percorso: string, corpo: unknown) =>
    richiesta<T>("PUT", percorso, corpo),
  elimina: <T>(percorso: string) => richiesta<T>("DELETE", percorso),
};

export const autenticazione = {
  chiSono: () => api.leggi<Utente>("/api/auth/me"),
  entra: (email: string, password: string) =>
    api.scrivi<Utente | RichiestaSecondoFattore>("/api/auth/login", { email, password }),
  verificaSecondoFattore: (token_accesso: string, codice: string) =>
    api.scrivi<Utente>("/api/auth/2fa/verifica", { token_accesso, codice }),
  avviaSecondoFattore: () => api.scrivi<AvvioSecondoFattore>("/api/auth/2fa/avvia"),
  confermaSecondoFattore: (codice: string) =>
    api.scrivi<{ codici: string[] }>("/api/auth/2fa/conferma", { codice }),
  esci: () => api.scrivi<{ ok: boolean }>("/api/auth/logout"),
  cambiaPassword: (password_attuale: string, nuova_password: string) =>
    api.scrivi<{ ok: boolean }>("/api/auth/cambia-password", {
      password_attuale,
      nuova_password,
    }),
};

/** Un accesso visto da chi lo gestisce: in più di `Utente` (la risposta di
 * `/api/auth/me`) porta `attivo`, che non serve a chi chiede solo "chi sono". */
export interface UtenteGestito
  extends Omit<Utente, "deve_attivare_secondo_fattore" | "pdf_abilitato"> {
  attivo: boolean;
}

/** La risposta della reimpostazione della password. `password_provvisoria`
 * arriva **solo in questa risposta**: il server non la conserva in chiaro, e la
 * pagina la mostra una volta sola (vedi `Utenti.tsx`). */
export interface PasswordReimpostata {
  utente: UtenteGestito;
  password_provvisoria: string;
}

export const utenti = {
  lista: () => api.leggi<UtenteGestito[]>("/api/utenti"),
  crea: (corpo: { nome: string; email: string; password: string; ruolo: string }) =>
    api.scrivi<UtenteGestito>("/api/utenti", corpo),
  cambiaRuolo: (id: string, ruolo: string) =>
    api.scrivi<UtenteGestito>(`/api/utenti/${id}/ruolo`, { ruolo }),
  disattiva: (id: string) => api.scrivi<UtenteGestito>(`/api/utenti/${id}/disattiva`),
  riattiva: (id: string) => api.scrivi<UtenteGestito>(`/api/utenti/${id}/riattiva`),
  reimpostaPassword: (id: string) =>
    api.scrivi<PasswordReimpostata>(`/api/utenti/${id}/reimposta-password`),
  reimpostaSecondoFattore: (id: string) =>
    api.scrivi<UtenteGestito>(`/api/utenti/${id}/reimposta-secondo-fattore`),
};

function conParametri(percorso: string, parametri: Record<string, string | undefined>) {
  const pezzi = Object.entries(parametri).filter(([, v]) => v !== undefined && v !== "");
  if (pezzi.length === 0) return percorso;
  const query = new URLSearchParams(pezzi as [string, string][]);
  return `${percorso}?${query.toString()}`;
}

export const anagrafica = {
  clienti: () => api.leggi<Cliente[]>("/api/clienti"),
  creaCliente: (corpo: {
    denominazione: string;
    sostituto_imposta: boolean;
    pubblica_amministrazione: boolean;
    split_payment: boolean;
    territorialita: string;
    codice: string;
    sede: string;
    indirizzo: string;
    codice_fiscale: string;
    partita_iva: string;
    codice_destinatario: string;
    pec: string;
  }) => api.scrivi<Cliente>("/api/clienti", corpo),
  /** L'anagrafica con il recapito per Fatture Smart. I documenti gia' nati non
   * cambiano: si sono congelati i dati del cliente quando sono stati creati. */
  correggiCliente: (
    id: string,
    corpo: {
      denominazione: string;
      sostituto_imposta: boolean;
      pubblica_amministrazione: boolean;
      split_payment: boolean;
      territorialita: string;
      codice: string;
      sede: string;
      indirizzo: string;
      codice_fiscale: string;
      partita_iva: string;
      codice_destinatario: string;
      pec: string;
    },
  ) => api.sostituisci<Cliente>(`/api/clienti/${id}`, corpo),
  /** Solo il recapito SDI. */
  cambiaRecapitoSdi: (id: string, codice_destinatario: string, pec: string) =>
    api.sostituisci<Cliente>(`/api/clienti/${id}/recapito-sdi`, { codice_destinatario, pec }),

  pratiche: (cliente_id?: string) =>
    api.leggi<Pratica[]>(conParametri("/api/pratiche", { cliente_id })),
  creaPratica: (corpo: {
    cliente_id: string;
    tipo: string;
    periodo: number;
    descrizione: string;
    responsabile: string;
    /** Stringa, come ogni importo. `null` vuol dire "si fattura a tempo". */
    prezzo_concordato: string | null;
  }) => api.scrivi<Pratica>("/api/pratiche", corpo),
  sospendi: (id: string) => api.scrivi<Pratica>(`/api/pratiche/${id}/sospendi`),
  riapri: (id: string) => api.scrivi<Pratica>(`/api/pratiche/${id}/riapri`),
  chiudi: (id: string, giorno: string) =>
    api.scrivi<Pratica>(`/api/pratiche/${id}/chiudi`, { giorno }),

  catalogo: () => api.leggi<Attivita[]>("/api/catalogo"),
  /** Quanto e quando e' usato ogni codice: per riconoscere i doppioni dimenticati. */
  usoCatalogo: () => api.leggi<UsoAttivita[]>("/api/catalogo/uso"),
  /** Le prestazioni che somigliano a quella che si sta per proporre. */
  simili: (descrizione: string, tipo?: string) =>
    api.leggi<PrestazioneSimile[]>(conParametri("/api/catalogo/simili", { descrizione, tipo })),
  /** Chi imputa e non trova la prestazione la propone: il codice lo genera il server, vale a ore. */
  proponi: (corpo: { descrizione: string; tipo: string; diversa?: boolean }) =>
    api.scrivi<Attivita>("/api/catalogo/proposte", corpo),
  /** I codici proposti e non ancora rivisti: la coda di chi fattura. */
  daRivedere: () => api.leggi<CodiceDaRivedere[]>("/api/catalogo/da-rivedere"),
  /** Conferma un codice proposto: nome, tipi e la frase che finisce in fattura (obbligatoria). */
  rivedi: (codice: string, corpo: { descrizione: string; testo_fattura: string; tipi_pratica: string[] }) =>
    api.scrivi<Attivita>(`/api/catalogo/${encodeURIComponent(codice)}/rivedi`, corpo),
  creaAttivita: (corpo: {
    codice: string;
    descrizione: string;
    tipi_pratica: string[];
    prezzo: string | null;
    unita: string;
    testo_fattura?: string;
  }) => api.scrivi<Attivita>("/api/catalogo", corpo),
  /** La frase con cui un'attivita' compare nel testo per Fatture Smart. */
  cambiaTestoFattura: (codice: string, testo_fattura: string) =>
    api.sostituisci<Attivita>(`/api/catalogo/${encodeURIComponent(codice)}/testo`, {
      testo_fattura,
    }),
  /** Prezzo fisso di un'attivita' (null: a ore). Serve la capacita' del listino. */
  cambiaPrezzo: (codice: string, prezzo: string | null, unita: string) =>
    api.sostituisci<Attivita>(`/api/catalogo/${encodeURIComponent(codice)}/prezzo`, {
      prezzo,
      unita,
    }),
  /** La pratica su cui va un'attivita' per un cliente, creata vuota se manca.
   * `tipo` serve solo se l'attivita' vale per piu' tipi, `periodo` per
   * scegliere l'anno invece di quello proposto. */
  perImputare: (corpo: {
    cliente_id: string;
    attivita_codice: string;
    tipo?: string;
    periodo?: number;
  }) => api.scrivi<Pratica>("/api/pratiche/per-imputare", corpo),
  /** La pratica di assistenza ordinaria del cliente per quest'anno, creata se manca. */
  assistenzaOrdinaria: (cliente_id: string) =>
    api.scrivi<Pratica>("/api/pratiche/assistenza-ordinaria", { cliente_id }),
};

export interface DaImputare {
  pratica_id: string;
  giorno: string;
  minuti: number;
  attivita_codice: string;
  oggetto: string;
  scopo: string;
  /** Solo per un'attivita' a prezzo per unita'; 1 altrimenti. */
  quantita: number;
}

export const ore = {
  /** Le proprie, dentro una finestra di date con gli estremi inclusi.
   *
   * La finestra non e' una comodita': senza, ogni apertura della schermata
   * si tira dietro tutto lo storico della persona. */
  mie: (dal?: string, al?: string) =>
    api.leggi<Imputazione[]>(conParametri("/api/imputazioni/mie", { dal, al })),

  imputa: (corpo: DaImputare) => api.scrivi<Imputazione>("/api/imputazioni", corpo),

  /** La persona non si manda mai: la mette il server leggendola dalla
   * sessione, e il payload non ha nemmeno un campo dove scriverla. */
  correggi: (identificativo: string, corpo: DaImputare) =>
    api.sostituisci<Imputazione>(`/api/imputazioni/${identificativo}`, corpo),

  /** Il rapporto di ogni persona che ha imputato almeno un'ora, una
   * richiesta sola. Solo l'amministratore lo vede: vedi RACCOLTA_METRICHE
   * in `core/capacita.py`. */
  rapporti: () => api.leggi<Rapporto[]>("/api/imputazioni/rapporti"),

  /** Il registro delle ore con i filtri. Chi non vede le ore di tutti riceve
   * solo le sue, qualunque persona chieda. */
  registro: (filtro: FiltroRegistro) =>
    api.leggi<Registro>(
      conParametri("/api/imputazioni/registro", filtro as Record<string, string | undefined>),
    ),
  /** Le stesse ore, con gli stessi filtri, in un Excel con tabella, totali e
   * riepilogo. I prezzi ci sono solo per chi vede i documenti: lo decide il server. */
  esportaExcel: (filtro: FiltroRegistro, nomeFile: string) =>
    scaricaFile(
      conParametri("/api/imputazioni/registro.xlsx", filtro as Record<string, string | undefined>),
      nomeFile,
    ),
};

/** Il registro degli incassi: i versamenti che non hanno prodotto una fattura da soli,
 * l'annullo di un versamento e la situazione di un cliente. Registrare un incasso e'
 * `documenti.incassa`. */
export const incassi = {
  /** Gli acconti e i saldi da passare a mano su Fatture Smart, dal piu' vecchio. */
  daFatturare: () => api.leggi<VersamentoDaFatturare[]>("/api/incassi/da-fatturare"),
  /** Testo e cifre della fattura di un acconto o saldo, quelli di quel versamento. */
  fatturaDelGruppo: (gruppo: string) =>
    api.leggi<FatturaDelVersamento>(`/api/incassi/gruppo/${gruppo}/fattura`),
  /** Annulla un versamento, che resta visibile col motivo. */
  annulla: (id: string, motivo: string) =>
    api.scrivi<Incasso>(`/api/incassi/${id}/annulla`, { motivo }),
  /** Il numero che Fatture Smart ha dato alla fattura fatta a mano su un incasso. */
  segnaFatturato: (gruppo: string, riferimento: string) =>
    api.scrivi<Incasso[]>(`/api/incassi/gruppo/${gruppo}/fatturato`, { riferimento }),
  /** Quanto il cliente deve ancora e la storia dei suoi pagamenti. */
  situazioneCliente: (clienteId: string) =>
    api.leggi<SituazioneCliente>(`/api/clienti/${clienteId}/incassi`),
};

/** Il controllo di gestione, per l'amministratore. Il Periodo e' anche di chi fattura, senza le righe per persona. */
export const controllo = {
  /** Le pratiche a prezzo concordato il cui costo si avvicina al prezzo. Solo l'amministratore: usa i costi. */
  praticheAPrezzo: () => api.leggi<PrezzoConcordato>("/api/controllo/pratiche-a-prezzo"),
  /** Con `dal` e `al` insieme vince l'intervallo (semestri, trimestri, anno a cavallo); senza, l'anno. */
  gestione: (anno?: number, dal?: string, al?: string) =>
    api.leggi<Gestione>(conParametri("/api/controllo/gestione", { anno: anno?.toString(), dal, al })),
  /** Lavoro, avvisi, incassato e i due buchi di un periodo, per tutti i clienti o per uno. */
  periodo: (dal: string, al: string, clienteId?: string) =>
    api.leggi<PeriodoControllo>(conParametri("/api/controllo/periodo", { dal, al, cliente_id: clienteId })),
  /** Lo stesso periodo in un Excel, con gli stessi filtri della pagina. `solo` apre uno dei due buchi:
   * senza, il file ha tutto il periodo. */
  esportaPeriodo: (
    dal: string,
    al: string,
    clienteId: string | undefined,
    solo: "da_incassare" | "da_fatturare" | undefined,
    nomeFile: string,
  ) =>
    scaricaFile(
      conParametri("/api/controllo/periodo.xlsx", { dal, al, cliente_id: clienteId, solo }),
      nomeFile,
    ),
  /** Le ultime modifiche a listino e catalogo, fatte da chi li tiene. */
  modificheListino: () => api.leggi<ModificaListino[]>("/api/controllo/listino-modifiche"),
};

/** Il costo orario dei dipendenti: solo l'amministratore, dato delicato. */
export const costi = {
  elenco: () => api.leggi<PersonaCosti[]>("/api/costi"),
  /** Imposta il costo da un giorno in poi, o corregge la cifra se quel giorno c'e' gia' una riga. */
  imposta: (persona: string, oraria: string, dal: string, nota = "") =>
    api.sostituisci<CostoRiga>("/api/costi", { persona, oraria, dal, nota }),
  togli: (id: number) => api.elimina<void>(`/api/costi/${id}`),
};

export interface CorpoTariffa {
  oraria: string;
  dal: string;
  tipo_pratica: string | null;
  cliente_id: string | null;
  persona: string | null;
  nota: string;
}

export const listino = {
  tariffe: () => api.leggi<Tariffa[]>("/api/tariffe"),
  /** Cosa succederebbe salvando: non scrive niente. Le dimensioni lasciate a
   * `null` valgono "per tutti". `oraria` e' una stringa. */
  anteprima: (corpo: CorpoTariffa) =>
    api.scrivi<AnteprimaTariffa>("/api/tariffe/anteprima", corpo),
  /** Aggiunge la tariffa, o ne corregge la cifra se c'e' gia' la stessa per lo
   * stesso giorno. `conferma` solo dopo aver mostrato l'avviso di una cifra fuori scala. */
  crea: (corpo: CorpoTariffa & { conferma?: boolean }) => api.scrivi<Tariffa>("/api/tariffe", corpo),
  /** Toglie una riga inserita per sbaglio. `conferma` se le ore resterebbero senza prezzo. */
  togli: (id: number, conferma = false) =>
    api.elimina<void>(`/api/tariffe/${id}${conferma ? "?conferma=true" : ""}`),
};

export interface NuovoAvviso {
  pratiche_id: string[];
  descrizione: string;
  data?: string;
  spese: { descrizione: string; importo: string }[];
  forfettarie: { descrizione: string; importo: string }[];
  riaddebita_bollo: boolean;
  /** Assente: come dice l'anagrafica del cliente. */
  ritenuta?: boolean;
}

/** Lo stato del backup, per la striscia dell'amministratore. Il messaggio e'
 * gia' scritto dal server (`jobs/backup_notturno.py`, `valuta`): qui non si
 * decide niente, si mostra. */
export interface StatoBackup {
  /** `ok`, `vecchio`, `assente`, `fallito`, `irraggiungibile`,
   * `non_configurato` o `non_applicabile`. */
  stato: string;
  da_segnalare: boolean;
  messaggio: string | null;
  /** ISO, UTC. */
  ultimo: string | null;
  ore: number | null;
}

export const backup = {
  stato: () => api.leggi<StatoBackup>("/api/backup/stato"),
};

export const documenti = {
  /** Il maturato di tutte le pratiche con qualcosa da fatturare, in una
   * richiesta sola. Prima esisteva solo una pratica per volta. */
  daFatturare: (cliente_id?: string) =>
    api.leggi<Maturato[]>(conParametri("/api/maturato", { cliente_id })),

  elenco: () => api.leggi<Documento[]>("/api/documenti"),
  /** Tutto cio' che e' stato scritto a un cliente, dal piu' recente. */
  delCliente: (clienteId: string) =>
    api.leggi<Documento[]>(`/api/documenti/del-cliente/${encodeURIComponent(clienteId)}`),
  dettaglio: (id: string) => api.leggi<Documento>(`/api/documenti/${id}`),
  avvisiAperti: () => api.leggi<Documento[]>("/api/documenti/avvisi-aperti"),
  /** I progressivi mancanti, anno per anno e serie per serie. Un elenco
   * vuoto è la buona notizia: nessuna combinazione ha buchi. */
  buchi: () => api.leggi<BucoNumerazione[]>("/api/documenti/buchi"),
  /** Il maturato da fatturare diviso per eta' del lavoro, e le pratiche
   * ferme da guardare per prime. Il totale e' lo stesso di `daFatturare`. */
  anzianita: () => api.leggi<Anzianita>("/api/maturato/anzianita"),
  /** Cinque numeri per il cruscotto: quanti clienti, prestazioni (e da rivedere) e documenti ci sono. Al posto
   * di leggere gli elenchi interi solo per sapere se sono vuoti. */
  statoAvvio: () => api.leggi<StatoAvvio>("/api/documenti/stato-avvio"),

  creaAvviso: (corpo: NuovoAvviso) => api.scrivi<Documento>("/api/documenti/avvisi", corpo),
  emetti: (id: string) => api.scrivi<Documento>(`/api/documenti/${id}/emetti`),
  /** Annulla un avviso e libera le sue ore nella stessa transazione. */
  annulla: (id: string) => api.scrivi<Documento>(`/api/documenti/${id}/annulla`),
  /** Registra un incasso, intero o parziale, anche diviso fra piu' modalita'.
   * **Incasso intero in un gesto solo**: restituisce la fattura, in bozza e senza
   * numero, da passare su Fatture Smart. **Acconto, o saldo dopo degli acconti**:
   * restituisce l'avviso (aperto con il suo residuo, o chiuso senza fattura) e i
   * versamenti restano da passare a mano. `parziale` e' la spunta: senza, una cifra
   * minore del residuo e' un errore. */
  incassa: (
    id: string,
    corpo: {
      data_incasso: string;
      versamenti: { importo: string; modalita: NomeModalita; nota?: string }[];
      parziale: boolean;
    },
  ) => api.scrivi<Documento>(`/api/documenti/${id}/incassa`, corpo),
  /** Le fatture incassate che aspettano Fatture Smart, dalla piu' vecchia. */
  daPassare: () => api.leggi<DaPassare[]>("/api/documenti/da-passare"),
  scheda: (id: string) => api.leggi<Scheda>(`/api/documenti/${id}/scheda`),
  /** Il testo da incollare nel campo Descrizione di Fatture Smart. */
  testo: (id: string) => api.leggi<TestoFatturaSmart>(`/api/documenti/${id}/testo`),
  /** Salva il testo ritoccato, finche' il documento non e' partito. */
  salvaTesto: (id: string, testo: string) =>
    api.sostituisci<TestoFatturaSmart>(`/api/documenti/${id}/testo`, { testo }),
  /** Il numero che Fatture Smart ha dato alla fattura: "93", oppure "1/PA". */
  numera: (id: string, numero: string) =>
    api.scrivi<Documento>(`/api/documenti/${id}/numero`, { numero }),
  /** Restituisce la nota di credito, fatta su Fatture Smart: serve il suo numero. */
  storna: (id: string, data: string, motivo: string, numero_nota: string) =>
    api.scrivi<Documento>(`/api/documenti/${id}/storna`, { data, motivo, numero_nota }),

  /** Il percorso del PDF, non una funzione che lo scarica: il cookie di
   * sessione viaggia da solo su una navigazione same-origin, quindi basta
   * un link normale (`<a href>` o `window.open`), niente `fetch`. */
  stampaUrl: (id: string) => `/api/documenti/${id}/stampa`,
};
